import { readonly, ref } from 'vue'
import type { SfxName } from '~/utils/sfxSamples'

// A device preference independent of both audio switches. Native haptic
// engines need @capacitor/haptics installed and synced; until then, use
// the browser vibration API where available and stay silent elsewhere.
const HAPTICS_KEY = 'elemental-dispatch-haptics-enabled'
const enabled = ref(true)
let loaded = false

// Short pulses for ordinary actions; a double pulse celebrates a reward.
const PATTERNS: Record<SfxName, number | number[]> = {
  tap: 8,
  select: 16,
  deselect: 6,
  confirm: 16,
  coin: 8,
  goldAppear: 8,
  trainingAppear: 16,
  hit: 35,
  victory: [20, 45, 30],
  epic: 16,
  legendary: [20, 45, 30],
}

function load() {
  if (loaded || typeof window === 'undefined') return
  loaded = true
  try {
    enabled.value = localStorage.getItem(HAPTICS_KEY) !== '0'
  } catch { /* unavailable storage keeps this session usable */ }
}

function vibrate(pattern: number | number[]) {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return
    if (navigator.userActivation?.hasBeenActive === false) return
    navigator.vibrate(pattern)
  } catch { /* unsupported hardware / denied vibration must not interrupt play */ }
}

function play(name: SfxName) {
  load()
  if (!enabled.value || typeof document === 'undefined' || document.hidden) return
  vibrate(PATTERNS[name])
}

function setEnabled(on: boolean) {
  load()
  enabled.value = on
  try { localStorage.setItem(HAPTICS_KEY, on ? '1' : '0') } catch { /* this session only */ }
  if (!on) vibrate(0)
}

export function useHaptics() {
  load()
  return { enabled: readonly(enabled), setEnabled, play }
}
